import React, { useMemo, useState } from 'react';
import { Box, Card, Chip, Grid } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import DataTable from '../../../sharedModules/dataTable';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { getCollectionsSelectedLocation } from '../../navigation/components/CollectionsLocationToggle';
import { useCollectionEmailSends } from '../hooks/useCollectionEmailSends';
import { CollectionEmailSendListItem, CollectionEmailSendStatus, STATUS_LABELS, SOURCE_LABELS } from '../model';
import { TONE_LABELS } from '../../config/model';
import EmailHistoryFilters, { FilterFields } from '../components/EmailHistoryFilters';
import EmailHistoryDetailModal from '../components/EmailHistoryDetailModal';

const statusChipColor = (status: CollectionEmailSendStatus): 'success' | 'error' | 'default' => {
  if (status === 'sent') return 'success';
  if (status === 'failed') return 'error';
  return 'default';
};

const buildColumns = () => [
  { name: 'Company', selector: (row: CollectionEmailSendListItem) => row.company, sortable: true, grow: 2 },
  { name: 'Bill Code', selector: (row: CollectionEmailSendListItem) => row.bill_code, sortable: true, width: '120px' },
  {
    name: 'Tone',
    selector: (row: CollectionEmailSendListItem) => row.tone,
    cell: (row: CollectionEmailSendListItem) => (
      <Chip label={TONE_LABELS[row.tone]} size="small" />
    ),
    width: '160px',
  },
  {
    name: 'Status',
    selector: (row: CollectionEmailSendListItem) => row.status,
    cell: (row: CollectionEmailSendListItem) => (
      <Chip label={STATUS_LABELS[row.status]} color={statusChipColor(row.status)} size="small" />
    ),
    width: '110px',
  },
  {
    name: 'Source',
    selector: (row: CollectionEmailSendListItem) => row.send_source,
    cell: (row: CollectionEmailSendListItem) => (
      <Chip label={SOURCE_LABELS[row.send_source]} size="small" variant="outlined" />
    ),
    width: '120px',
  },
  { name: 'Sent At', selector: (row: CollectionEmailSendListItem) => row.delivered_at ?? '—', width: '180px' },
];

const PER_PAGE_DEFAULT = 25;

const CollectionEmailHistoryPage: React.FC = () => {
  const [location] = useState(() => getCollectionsSelectedLocation());
  const [filters, setFilters] = useState<FilterFields>({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_DEFAULT);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const cleanedFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== '' && v != null),
    ) as Partial<FilterFields>;
  }, [filters]);

  const { data, isLoading, isError } = useCollectionEmailSends({
    location,
    page,
    per_page: perPage,
    ...cleanedFilters,
  });

  const rows = data?.data?.result ?? [];
  const totalRecords = data?.data?.total_records ?? 0;

  const handleFiltersChange = (newFilters: FilterFields) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
  };

  const handleRowsPerPageChange = (nextPerPage: number) => {
    setPerPage(nextPerPage);
    setPage(1);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Collections — Email History" titleIcon={<HistoryIcon />} />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <EmailHistoryFilters onChange={handleFiltersChange} />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2.5 }}>
        {isError && (
          <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
            Failed to load email history.
          </Box>
        )}
        <Card>
          <DataTable
            keyField="id"
            columns={buildColumns()}
            data={rows}
            progressPending={isLoading}
            pagination
            paginationServer
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            paginationTotalRows={totalRecords}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleRowsPerPageChange}
            highlightOnHover
            striped
            noHeader
            pointerOnHover
            onRowClicked={(row: CollectionEmailSendListItem) => setSelectedId(row.id)}
            noDataComponent={<Box sx={{ p: 3, color: 'text.secondary' }}>No email history found.</Box>}
          />
        </Card>
      </Grid>

      <EmailHistoryDetailModal
        open={selectedId !== null}
        emailSendId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </Grid>
  );
};

export default CollectionEmailHistoryPage;
